import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DomainSearch from '../components/domain/DomainSearch';
import DomainExtensionsModal from '../components/domain/DomainExtensionsModal';
import { FaCheck, FaTimes, FaShieldAlt, FaRocket, FaHeadset, FaGlobe, FaLock, FaSyncAlt } from 'react-icons/fa';
import SEOHelmet from '../components/SEOHelmet';

const Domains: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [initialSearch, setInitialSearch] = useState('');
  const [showExtensionsModal, setShowExtensionsModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const searchQuery = searchParams.get('search');
    if (searchQuery) {
      setInitialSearch(searchQuery);
    }
  }, [searchParams]);

  const domainExtensions = [
    { tld: '.com', price: 15.00, popular: true },
    { tld: '.net', price: 18.00, popular: true },
    { tld: '.org', price: 16.00, popular: true },
    { tld: '.io', price: 64.00, popular: true },
    { tld: '.co', price: 29.99, popular: false },
    { tld: '.dev', price: 19.99, popular: false },
    { tld: '.app', price: 24.99, popular: false },
    { tld: '.info', price: 9.99, popular: false },
    { tld: '.biz', price: 17.99, popular: false },
    { tld: '.me', price: 19.99, popular: false },
    { tld: '.tech', price: 49.99, popular: false },
    { tld: '.store', price: 34.99, popular: false },
  ];

  const features = [
    {
      icon: FaLock,
      title: 'Free Domain Privacy',
      description: 'Protect your personal information with WHOIS privacy protection at no extra cost'
    },
    {
      icon: FaShieldAlt,
      title: 'DNS Management',
      description: 'Full DNS control with easy-to-use management tools and instant propagation'
    },
    {
      icon: FaSyncAlt,
      title: 'Easy Domain Transfer',
      description: 'Transfer your existing domains to us with zero downtime and competitive pricing'
    },
    {
      icon: FaRocket,
      title: 'Instant Setup',
      description: 'Your domain is active immediately after registration with automatic configuration'
    },
    {
      icon: FaGlobe,
      title: '400+ Extensions',
      description: 'Choose from hundreds of domain extensions to find the perfect match for your brand'
    },
    {
      icon: FaHeadset,
      title: '24/7 Support',
      description: 'Get expert assistance anytime with our round-the-clock customer support team'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Domain Registration",
    "description": "Register your perfect domain name with 12and22. Get free privacy protection, DNS management, and 24/7 support.",
    "brand": {
      "@type": "Brand",
      "name": "12and22"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "lowPrice": "9.99",
      "highPrice": "64.00",
      "offerCount": "10"
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHelmet
        title="Domain Registration - Buy & Register Domain Names | 12and22"
        description="Register your perfect domain name starting at $9.99/year. Get free WHOIS privacy protection, DNS management, email forwarding, and 24/7 support with every domain at 12and22."
        keywords="domain registration, buy domain, domain name, cheap domains, domain search, .com domain, .net domain, .org domain, domain privacy, DNS management, 12and22"
        canonicalUrl="https://12and22.com/domains"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Left Column - Text Content */}
            <div>
              {/* Badge */}
              <div className="inline-flex items-center px-4 py-2 mb-8 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-full">
                <span className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full mr-3"></span>
                <span className="text-sm font-medium text-gray-700">Over 1 Million Domains Registered</span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-8 text-gray-900">
                Your Perfect
                <span className="text-primary-navy block">Domain <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">Awaits</span></span>
              </h1>

              {/* Subtitle */}
              <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-2xl">
                Secure your online identity with a memorable domain name. Instant registration, 
                free privacy protection, and powerful DNS management included.
              </p>

              {/* Trust Indicators */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
                {[
                  { 
                    icon: <FaShieldAlt className="w-5 h-5" />, 
                    text: 'WHOIS Privacy', 
                    subtext: 'Always Free' 
                  },
                  { 
                    icon: <FaGlobe className="w-5 h-5" />, 
                    text: '400+ TLDs', 
                    subtext: 'Wide Selection' 
                  },
                  { 
                    icon: <FaLock className="w-5 h-5" />, 
                    text: 'SSL Ready', 
                    subtext: 'Secure by Default' 
                  },
                  { 
                    icon: <FaSyncAlt className="w-5 h-5" />, 
                    text: 'Auto-Renewal', 
                    subtext: 'Never Expire' 
                  },
                  { 
                    icon: <FaRocket className="w-5 h-5" />, 
                    text: 'Instant Setup', 
                    subtext: 'No Waiting' 
                  },
                  { 
                    icon: <FaHeadset className="w-5 h-5" />, 
                    text: '24/7 Support', 
                    subtext: 'Always Here' 
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center text-gray-600 border border-gray-200">
                      {item.icon}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{item.text}</div>
                      <div className="text-xs text-gray-500">{item.subtext}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right Column - Domain Search Card */}
            <div>
              <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
                {/* Glossy overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
                <div className="relative z-10">
                  {/* Card Header */}
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 via-purple-500 to-blue-600 rounded-2xl mb-4 shadow-lg relative overflow-hidden">
                      {/* Icon glossy overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                      <FaGlobe className="w-8 h-8 text-white relative z-10" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Search Your Domain</h3>
                    <p className="text-gray-600">Check availability instantly</p>
                  </div>

                  {/* Domain Search Component */}
                  <DomainSearch initialSearch={initialSearch} />

                  {/* Popular Extensions */}
                  <div className="mt-8 text-center">
                    <p className="text-gray-500 text-sm mb-4">Most popular extensions</p>
                    <div className="grid grid-cols-3 gap-3">
                      {domainExtensions.filter(ext => ext.popular).slice(0, 6).map((ext) => (
                        <div
                          key={ext.tld}
                          className="bg-white border border-gray-200 hover:border-primary-navy rounded-lg px-4 py-3 text-center transition-all duration-200 hover:shadow-md cursor-pointer group"
                        >
                          <div className="font-semibold text-gray-900 group-hover:text-primary-navy">{ext.tld}</div>
                          <div className="text-sm text-gray-500">${ext.price}/yr</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Domain Extensions Pricing */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-full">
              <span className="w-2 h-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mr-3"></span>
              <span className="text-sm font-medium text-gray-700">Transparent Pricing</span>
            </div>
            
            <h2 className="text-4xl font-black text-gray-900 mb-4">
              Domain Extension Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose from a wide variety of domain extensions at competitive prices
            </p>
          </div>

          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none rounded-2xl"></div>
            
            <div className="relative z-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {domainExtensions.map((ext) => (
                <div
                  key={ext.tld}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all duration-200 hover:border-primary-navy group cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-gray-800 group-hover:text-primary-navy">{ext.tld}</span>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500 font-bold">
                      ${ext.price}/yr
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <p className="text-gray-600 mb-4">
                Can't find the extension you're looking for?
              </p>
              <button 
                onClick={() => setShowExtensionsModal(true)}
                className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-lg font-bold hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
                <span className="relative z-10">View All 400+ Extensions</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-full">
              <span className="w-2 h-2 bg-gradient-to-r from-green-400 to-blue-500 rounded-full mr-3"></span>
              <span className="text-sm font-medium text-gray-700">Premium Features Included</span>
            </div>
            
            <h2 className="text-4xl font-black text-gray-900 mb-4">
              Why Choose 12and22 for Your Domain?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get more than just a domain name with our comprehensive features
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 relative overflow-hidden group">
                  {/* Glossy overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none rounded-2xl group-hover:from-white/30 transition-all duration-200"></div>
                  
                  <div className="relative z-10">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-xl flex items-center justify-center shadow-md relative overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-xl"></div>
                          <Icon className="text-white text-xl relative z-10" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-gray-600">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Domain Transfer Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 rounded-2xl p-12 text-white shadow-2xl relative overflow-hidden">
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none rounded-2xl"></div>
            
            <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 mb-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full">
                  <span className="w-2 h-2 bg-gradient-to-r from-green-400 to-blue-400 rounded-full mr-3"></span>
                  <span className="text-sm font-medium">Easy Domain Transfer</span>
                </div>
                
                <h2 className="text-4xl font-black mb-4">
                  Transfer Your Domain to 12and22
                </h2>
                <p className="text-xl mb-6 text-blue-100">
                  Already have a domain? Transfer it to us and enjoy better pricing, 
                  superior support, and advanced management tools.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center">
                    <FaCheck className="mr-3 text-green-400" />
                    <span>Free domain transfer - no hidden fees</span>
                  </li>
                  <li className="flex items-center">
                    <FaCheck className="mr-3 text-green-400" />
                    <span>No downtime during transfer</span>
                  </li>
                  <li className="flex items-center">
                    <FaCheck className="mr-3 text-green-400" />
                    <span>1 year extension included with transfer</span>
                  </li>
                  <li className="flex items-center">
                    <FaCheck className="mr-3 text-green-400" />
                    <span>Free WHOIS privacy protection</span>
                  </li>
                </ul>
              </div>
              
              <div className="text-center">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-white/20 to-white/10 rounded-2xl mb-4">
                    <FaSyncAlt className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-2xl font-bold mb-4">Start Your Transfer</p>
                  <p className="text-blue-100 mb-6">Transfer process takes only 5-7 days</p>
                  <button
                    onClick={() => navigate('/domain-transfer')}
                    className="w-full px-6 py-4 bg-white text-primary-navy rounded-xl hover:bg-gray-100 transition-all duration-200 font-bold shadow-lg relative overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-white/10 group-hover:from-white/10 transition-all duration-200 rounded-xl"></div>
                    <span className="relative z-10">Transfer Domain Now</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-12 text-center shadow-xl relative overflow-hidden">
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
            <div className="relative z-10">
              <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-full">
                <span className="w-2 h-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mr-3"></span>
                <span className="text-sm font-semibold text-gray-700">Get Started Today</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
                Ready to Secure Your Domain?
              </h2>
              
              <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
                Don't wait - your perfect domain might be taken tomorrow. 
                Register now and start building your online presence today.
              </p>
              
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-10 py-4 rounded-lg font-bold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
                <span className="relative z-10">Search for Your Domain</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Domain Extensions Modal */}
      <DomainExtensionsModal 
        isOpen={showExtensionsModal} 
        onClose={() => setShowExtensionsModal(false)} 
      />
    </div>
  );
};

export default Domains;